import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {useGuideAudioController, useGuidePreferences} from "@khan/guide-core";
import {guideVoices} from "@khan/guide-utils";
import {doNotTranslate} from "@khan/i18n";

import speakerIcon from "@phosphor-icons/core/regular/speaker-simple-high.svg";

interface Props {
    style?: StyleType;
    currentVoice: string | null;
    onChange: (value: string) => void;
    isLoading?: boolean;
}

/**
 * Allow users to set a preferred voice for AI Guide.
 *
 * Renders a SingleSelect with various voice options for users to choose from
 * and includes a "Listen" button that plays a sample of the selected voice.
 * This is used in the Preferences panel.
 */
const GuideVoiceSetting = ({
    style,
    currentVoice,
    onChange,
    isLoading,
}: Props): React.ReactElement | null => {
    const {isTextToSpeechSupported, isPlaying, play, stop} =
        useGuideAudioController();
    const {preferences} = useGuidePreferences();

    const handlePlayPause = () => {
        if (!currentVoice) {
            return;
        }

        if (isPlaying) {
            stop();
        } else {
            const voiceSampleId = `${currentVoice}:${preferences.guideLanguageCode}`;
            play(voiceSampleId);
        }
    };

    if (!isTextToSpeechSupported) {
        return null;
    }

    return (
        <View style={style}>
            <LabelLarge tag="label" htmlFor="select-guide-voice">
                {t`Voice`}
            </LabelLarge>
            <LabelSmall style={styles.subtitle}>
                {t`Choose the voice you'd like Khanmigo to talk to you with.`}
            </LabelSmall>
            <View style={styles.row}>
                <View style={styles.selectContainer}>
                    <SingleSelect
                        id="select-guide-voice"
                        style={styles.select}
                        placeholder={isLoading ? t`Loading...` : t`Select one`}
                        selectedValue={currentVoice}
                        onChange={(value) => {
                            onChange(value);
                            stop();
                        }}
                    >
                        {guideVoices.map((descriptor, index) => {
                            return (
                                <OptionItem
                                    key={descriptor}
                                    label={doNotTranslate`Khanmigo ${index + 1}`}
                                    value={descriptor}
                                />
                            );
                        })}
                    </SingleSelect>
                </View>
                <Button
                    style={styles.listenButton}
                    kind="secondary"
                    onClick={handlePlayPause}
                    endIcon={speakerIcon}
                    disabled={!currentVoice}
                >
                    {isPlaying ? t`Pause` : t`Listen`}
                </Button>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    listenButton: {
        width: "fit-content",
    },
    row: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.medium_16,
    },
    select: {
        width: "100%",
    },
    selectContainer: {
        flex: 1,
        maxInlineSize: 400,
    },
    subtitle: {
        color: color.offBlack64,
    },
});

export default React.memo(GuideVoiceSetting);
